/**
 * Auto Tax Products - Ensures _tax_ products are present in cart for each main product, no calculation.
 * Naming: <MAIN_PRODUCT_TITLE>_tax_<SITE_LABEL>
 * Regex: ^(.+?)_tax_(Gruene|Comal|Double|Huaco|Riverbank|Gruene (Weekend)|Comal (Weekend)|Double (Weekend))$
 */

(function() {
  // Polyfill for Element.closest() - needed for older browsers (IE, older Safari/Firefox)
  if (!Element.prototype.closest) {
    Element.prototype.closest = function(selector) {
      var el = this;
      while (el) {
        if (el.matches ? el.matches(selector) : el.msMatchesSelector(selector)) {
          return el;
        }
        el = el.parentElement || el.parentNode;
      }
      return null;
    };
  }

  // Polyfill for Element.matches() - needed by closest polyfill
  if (!Element.prototype.matches) {
    Element.prototype.matches = Element.prototype.msMatchesSelector || 
                                  Element.prototype.webkitMatchesSelector;
  }
  const ENABLED = true;
  const DEBUG = false;

  const pendingTaxAdditions = new Set();
  const pendingWristbandAdditions = new Set();
  const externalWristbandAdditions = new Set();
  const recentWristbandAdditions = new Map();
  const RECENT_WRISTBAND_ADDITION_WINDOW_MS = 2500;
  let isProcessingTaxCart = false;
  let queuedCartProcessing = false;
  let lastAutoTaxCartUpdate = 0;
  const AUTO_TAX_UPDATE_COOLDOWN_MS = 6000; // skip cart:updated reprocessing for 6s after our own additions

  const WRISTBAND_PRODUCTS = {
    // NB City locations - $2 fee, date-restricted
    'Gruene': 'nb-city-wristband',
    'The Double': 'nb-city-wristband',
    'Comal': 'nb-city-wristband',
    
    // WORD locations - $1 fee, always apply
    'Riverbank': 'word-wristband',
    'Huaco': 'word-wristband',
    
    'TEST': 'test-wristband-fee'
  };
  
  // Location groups for wristband requirements
  const NB_CITY_LOCATIONS = ['Gruene', 'The Double', 'Comal'];
  const WORD_LOCATIONS = ['Riverbank', 'Huaco'];

  const CHECKOUT_BUTTON_SELECTOR = '[href*="/checkout"], [name="checkout"], [data-checkout], .checkout-btn, .cart__checkout';
  const INITIAL_STATUS_CLASS = 'auto-tax-initial-status';
  const CHECKOUT_ENABLED_CLASS = 'auto-tax-checkout-enabled';

  let initialCheckoutLocked = true;
  let initialCheckoutObserver = null;

  function log(msg, data = '') {
    if (DEBUG) {
      if (data) {
        console.log('[AutoTax]', msg, data);
      } else {
        console.log('[AutoTax]', msg);
      }
    }
  }

  function markRecentWristbandAddition(location) {
    if (!location) return;
    recentWristbandAdditions.set(location, Date.now());
  }

  function setCheckoutEnabled(enabled) {
    const body = document.body;
    if (!body) {
      return;
    }
    if (enabled) {
      body.classList.add(CHECKOUT_ENABLED_CLASS);
    } else {
      body.classList.remove(CHECKOUT_ENABLED_CLASS);
    }
  }

  function getCheckoutButtons() {
    return Array.from(document.querySelectorAll(CHECKOUT_BUTTON_SELECTOR)).filter(Boolean);
  }

  function ensureInitialStatusMessage(button, options = {}) {
    const { message = 'Verifying required wristband and tax items…' } = options;
    let statusMessage = button.nextElementSibling;
    if (!statusMessage || !statusMessage.classList || !statusMessage.classList.contains(INITIAL_STATUS_CLASS)) {
      statusMessage = document.createElement('div');
      statusMessage.className = INITIAL_STATUS_CLASS;
      statusMessage.style.marginTop = '0.75rem';
      statusMessage.style.fontSize = '0.85em';
      statusMessage.style.fontStyle = 'italic';
      statusMessage.style.color = '#444';
      statusMessage.style.opacity = '0.95';
      button.insertAdjacentElement('afterend', statusMessage);
    }
    statusMessage.textContent = message;
    return statusMessage;
  }

  function removeInitialStatusMessages() {
    if (initialCheckoutLocked) {
      return;
    }
    document.querySelectorAll(`.${INITIAL_STATUS_CLASS}`).forEach(node => {
      if (node && node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });
  }

  function updateInitialStatusMessages(message, options = {}) {
    const { tone = 'info' } = options;
    document.querySelectorAll(`.${INITIAL_STATUS_CLASS}`).forEach(node => {
      node.textContent = message;
      if (tone === 'error') {
        node.style.color = '#b00020';
        node.style.fontWeight = '600';
        node.style.fontStyle = 'normal';
        node.style.opacity = '1';
      } else if (tone === 'success') {
        node.style.color = '#0a6a4a';
        node.style.fontWeight = '600';
        node.style.fontStyle = 'normal';
        node.style.opacity = '1';
      } else {
        node.style.color = '#444';
        node.style.fontWeight = '400';
        node.style.fontStyle = 'italic';
        node.style.opacity = '0.95';
      }
    });
  }

  function lockCheckoutButton(button) {
    if (!button || button.dataset.autoTaxInitialLock === 'true') {
      return;
    }

    button.dataset.autoTaxInitialLock = 'true';
    button.dataset.autoTaxInitialOriginalPointerEvents = button.style.pointerEvents || '';
    button.dataset.autoTaxInitialOriginalOpacity = button.style.opacity || '';
    button.dataset.autoTaxInitialOriginalFilter = button.style.filter || '';
    button.dataset.autoTaxInitialOriginalCursor = button.style.cursor || '';
    button.dataset.autoTaxInitialHadAriaDisabled = button.hasAttribute('aria-disabled') ? 'true' : 'false';
    button.dataset.autoTaxInitialAriaValue = button.getAttribute('aria-disabled') || '';

    if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
      button.dataset.autoTaxInitialOriginalDisabled = button.disabled ? 'true' : 'false';
    }

    button.setAttribute('aria-disabled', 'true');
    button.style.pointerEvents = 'none';
    button.style.opacity = '0.6';
    button.style.filter = 'grayscale(60%)';
    button.style.cursor = 'not-allowed';

    if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
      button.disabled = true;
    }

    ensureInitialStatusMessage(button);
  }

  function unlockCheckoutButton(button, options = {}) {
    if (!button || button.dataset.autoTaxInitialLock !== 'true') {
      return;
    }

    const { leaveStatus = false, statusMessage } = options;

    const pointerEvents = button.dataset.autoTaxInitialOriginalPointerEvents || '';
    const opacity = button.dataset.autoTaxInitialOriginalOpacity || '';
    const filter = button.dataset.autoTaxInitialOriginalFilter || '';
    const cursor = button.dataset.autoTaxInitialOriginalCursor || '';
    const hadAriaDisabled = button.dataset.autoTaxInitialHadAriaDisabled === 'true';
    const ariaValue = button.dataset.autoTaxInitialAriaValue || null;
    const originalDisabled = button.dataset.autoTaxInitialOriginalDisabled === 'true';

    button.style.pointerEvents = pointerEvents;
    button.style.opacity = opacity;
    button.style.filter = filter;
    button.style.cursor = cursor;

    if (!hadAriaDisabled) {
      button.removeAttribute('aria-disabled');
    } else if (ariaValue !== null && ariaValue !== '') {
      button.setAttribute('aria-disabled', ariaValue);
    }

    if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
      button.disabled = originalDisabled;
    }

    const nextSibling = button.nextElementSibling;
    if (nextSibling && nextSibling.classList && nextSibling.classList.contains(INITIAL_STATUS_CLASS)) {
      if (statusMessage) {
        nextSibling.textContent = statusMessage;
        nextSibling.style.fontStyle = 'normal';
        nextSibling.style.color = '#0a6a4a';
      } else if (!leaveStatus && nextSibling.parentNode) {
        nextSibling.parentNode.removeChild(nextSibling);
      }
    }

    delete button.dataset.autoTaxInitialLock;
    delete button.dataset.autoTaxInitialOriginalPointerEvents;
    delete button.dataset.autoTaxInitialOriginalOpacity;
    delete button.dataset.autoTaxInitialOriginalFilter;
    delete button.dataset.autoTaxInitialOriginalCursor;
    delete button.dataset.autoTaxInitialHadAriaDisabled;
    delete button.dataset.autoTaxInitialAriaValue;
    delete button.dataset.autoTaxInitialOriginalDisabled;
  }

  function applyInitialCheckoutBlock(options = {}) {
    const { force = false } = options;
    if (!initialCheckoutLocked && !force) {
      return;
    }
    if (force) {
      initialCheckoutLocked = true;
    }
    setCheckoutEnabled(false);
    // Immediately lock all checkout buttons to prevent race condition
    getCheckoutButtons().forEach(lockCheckoutButton);
    startCheckoutObserver();
    // Re-lock buttons after a brief delay to catch any that were added dynamically
    setTimeout(() => {
      if (initialCheckoutLocked) {
        getCheckoutButtons().forEach(lockCheckoutButton);
      }
    }, 100);
  }

  function releaseInitialCheckoutBlock(options = {}) {
    if (!initialCheckoutLocked) {
      setCheckoutEnabled(true);
      return;
    }
    initialCheckoutLocked = false;
    setCheckoutEnabled(true);
    if (initialCheckoutObserver) {
      initialCheckoutObserver.disconnect();
      initialCheckoutObserver = null;
    }
    getCheckoutButtons().forEach(button => unlockCheckoutButton(button, options));
  }

  function releaseInitialCheckoutBlockIfLocked(options = {}) {
    const {
      message = 'Required items verified. You may check out when ready.',
      tone = 'success',
      leaveStatus = true,
      delayMs = 2000 // Add extra 2-second delay before unlocking
    } = options;

    if (initialCheckoutLocked) {
      // Add a delay before releasing the checkout block to ensure cart is fully synced
      setTimeout(() => {
        releaseInitialCheckoutBlock({ statusMessage: message, leaveStatus });
        updateInitialStatusMessages(message, { tone });
        setTimeout(removeInitialStatusMessages, 3000);
      }, delayMs);
    } else {
      updateInitialStatusMessages(message, { tone });
      setTimeout(removeInitialStatusMessages, 3000);
    }
  }

  function startCheckoutObserver() {
    if (initialCheckoutObserver || !initialCheckoutLocked) {
      return;
    }
    try {
      initialCheckoutObserver = new MutationObserver(() => {
        if (initialCheckoutLocked) {
          applyInitialCheckoutBlock();
        }
      });
      initialCheckoutObserver.observe(document.body, { childList: true, subtree: true });
    } catch (observerError) {
      log('Failed to start checkout button observer: ' + observerError.message);
    }
  }

  function wasWristbandRecentlyAdded(location) {
    if (!location) return false;
    const timestamp = recentWristbandAdditions.get(location);
    if (!timestamp) return false;
    if (Date.now() - timestamp <= RECENT_WRISTBAND_ADDITION_WINDOW_MS) {
      return true;
    }
    recentWristbandAdditions.delete(location);
    return false;
  }

  document.addEventListener('rockinr:wristband-fee:add:start', event => {
    const location = event?.detail?.location;
    if (!location) return;
    externalWristbandAdditions.add(location);
    log(`External wristband addition started for '${location}'.`);
  });

  document.addEventListener('rockinr:wristband-fee:add:complete', event => {
    const location = event?.detail?.location;
    if (!location) return;
    externalWristbandAdditions.delete(location);
    const success = !!event?.detail?.success;
    if (success) {
      markRecentWristbandAddition(location);
      log(`External wristband addition completed for '${location}'.`);
    } else {
      log(`External wristband addition finished without success for '${location}'.`);
    }
  });

  // Check if current page or product is from the TEST collection (copied from wristband-fee.js)
  function isTestCollection() {
    const currentUrl = window.location.href.toLowerCase();
    if (currentUrl.includes('/collections/test')) {
      return true;
    }
    const productMeta = document.querySelector('meta[property="og:product:collection"], meta[name="collection"]');
    if (productMeta && productMeta.content && productMeta.content.toLowerCase().includes('test')) {
      return true;
    }
    return false;
  }

  // Get booking location from storage or TEST collection override (copied from wristband-fee.js)
  function getBookingLocation() {
    try {
      const data = JSON.parse(localStorage.getItem('rockinRBookingData') || '{}');
      if (data.location) {
        return data.location;
      }
      if (isTestCollection()) {
        return 'TEST';
      }
      return null;
    } catch (e) {
      log('Error reading booking data: ' + e.message);
      return null;
    }
  }
  
  // Get booking date from storage
  function getBookingDate() {
    try {
      const data = JSON.parse(localStorage.getItem('rockinRBookingData') || '{}');
      return data.date || null;
    } catch (e) {
      log('Error reading booking date: ' + e.message);
      return null;
    }
  }
  
  // Date calculation helpers for NB City wristband requirements
  function getMemorialDay(year) {
    // Memorial Day = Last Monday of May
    const may31 = new Date(year, 4, 31);
    const dayOfWeek = may31.getDay();
    // If May 31 is Monday (1), that's Memorial Day
    // If May 31 is Sunday (0), go back 6 days to Monday
    // Otherwise, go back (dayOfWeek - 1) days to get to Monday
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    return new Date(year, 4, 31 - daysToSubtract);
  }
  
  function getLaborDay(year) {
    // Labor Day = First Monday of September
    const sept1 = new Date(year, 8, 1);
    const dayOfWeek = sept1.getDay();
    // If Sept 1 is Monday (1), that's Labor Day
    // If Sept 1 is Sunday (0), add 1 day to get to Monday
    // Otherwise, add (8 - dayOfWeek) days to get to next Monday
    const daysToAdd = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;
    return new Date(year, 8, 1 + daysToAdd);
  }
  
  function isSameDate(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }
  
  function isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  }
  
  function isDateInRange(date, startDate, endDate) {
    return date >= startDate && date <= endDate;
  }
  
  // Check if date is Memorial Day or Labor Day (holidays requiring weekend menu)
  function isHolidayRequiringWeekendMenu(dateString) {
    if (!dateString) return false;
    
    try {
      // Parse date in local timezone
      const [year, month, day] = dateString.split('-').map(Number);
      const bookingDate = new Date(year, month - 1, day);
      
      // Get key dates for the year
      const memorialDay = getMemorialDay(year);
      const laborDay = getLaborDay(year);
      
      // Check if it's Memorial Day
      if (isSameDate(bookingDate, memorialDay)) {
        log('Memorial Day - weekend menu required');
        return true;
      }
      
      // Check if it's Labor Day
      if (isSameDate(bookingDate, laborDay)) {
        log('Labor Day - weekend menu required');
        return true;
      }
      
      log('Date is not a holiday requiring weekend menu');
      return false;
    } catch (e) {
      log('Error checking holiday status: ' + e.message);
      return false;
    }
  }
  
  // Check if NB City wristband is required for the given date
  function isNBCityWristbandRequired(dateString) {
    if (!dateString) return false;
    
    try {
      // Parse date in local timezone
      const [year, month, day] = dateString.split('-').map(Number);
      const bookingDate = new Date(year, month - 1, day);
      
      // Get key dates for the year
      const memorialDay = getMemorialDay(year);
      const laborDay = getLaborDay(year);
      const july4th = new Date(year, 6, 4); // July 4th
      
      // Check if it's Memorial Day
      if (isSameDate(bookingDate, memorialDay)) {
        log('Memorial Day - wristband required');
        return true;
      }
      
      // Check if it's July 4th
      if (isSameDate(bookingDate, july4th)) {
        log('July 4th - wristband required');
        return true;
      }
      
      // Check if it's Labor Day
      if (isSameDate(bookingDate, laborDay)) {
        log('Labor Day - wristband required');
        return true;
      }
      
      // Get Saturday and Sunday before Memorial Day
      const satBeforeMemorial = new Date(memorialDay);
      satBeforeMemorial.setDate(memorialDay.getDate() - 2);
      const sunBeforeMemorial = new Date(memorialDay);
      sunBeforeMemorial.setDate(memorialDay.getDate() - 1);
      
      // Check if it's the Saturday or Sunday before Memorial Day
      if (isSameDate(bookingDate, satBeforeMemorial) || isSameDate(bookingDate, sunBeforeMemorial)) {
        log('Weekend before Memorial Day - wristband required');
        return true;
      }
      
      // Check if it's a weekend between Memorial Day and Labor Day
      if (isWeekend(bookingDate) && isDateInRange(bookingDate, memorialDay, laborDay)) {
        log('Weekend during Memorial-Labor Day season - wristband required');
        return true;
      }
      
      log('Date does not require wristband');
      return false;
    } catch (e) {
      log('Error checking NB City wristband requirement: ' + e.message);
      return false;
    }
  }
  
  // Determine if wristband should be added based on location and date
  // storedDate parameter allows validation when localStorage is unavailable (e.g., at checkout)
  function shouldAddWristband(location, storedDate = null) {
    if (!location) return false;
    
    // TEST collection always requires wristband
    if (location === 'TEST') {
      log('TEST location - wristband required');
      return true;
    }
    
    // WORD locations always require wristband
    if (WORD_LOCATIONS.includes(location)) {
      log(`${location} is a WORD location - wristband always required`);
      return true;
    }
    
    // NB City locations require wristband only on specific dates
    if (NB_CITY_LOCATIONS.includes(location)) {
      // Try to get date from parameter first, then localStorage
      const bookingDate = storedDate || getBookingDate();
      if (!bookingDate) {
        // If no booking date is available we should NOT add/preserve an NB City wristband by default.
        // Returning false prevents adding a wristband when the date is unknown (fixes cases like Nov 11
        // where no booking date is present and a wristband was previously being added erroneously).
        log(`${location} is an NB City location but no booking date found - not adding wristband when date unavailable`);
        return false;
      }
      
      const required = isNBCityWristbandRequired(bookingDate);
      log(`${location} is an NB City location, date: ${bookingDate}, wristband required: ${required}`);
      return required;
    }
    
    log(`${location} does not match any wristband requirement rules`);
    return false;
  }

  // Use Booking Location from cart item properties
  function isPassProduct(item) {
    const productTitle = item.product_title || item.title || '';
    return productTitle.includes('Season Pass') || 
           productTitle.toLowerCase().includes('day pass') ||
           productTitle.toLowerCase().includes('river access');
  }

  function getSiteLabelForProduct(item) {
    if (item.properties && item.properties['Booking Location']) {
      return item.properties['Booking Location'];
    }
    
    // For pass products - using a default location for tax purposes
    // This allows them to checkout without booking details while still adding the gruene tax
    if (isPassProduct(item)) {
      return 'Gruene';
    }
    
    return null;
  }

  // --- Helper: Build tax product title from main product title and site label ---
  function buildTaxProductTitle(mainProductTitle, siteLabel) {
    // Remove "The " prefix from product title for tax product search (e.g., "The Double" becomes "Double_tax_...")
    let cleanedTitle = mainProductTitle;
    if (cleanedTitle.startsWith('The ')) {
      cleanedTitle = cleanedTitle.substring(4); // Remove "The " (4 characters)
      log(`Removed "The" prefix from product title: "${mainProductTitle}" -> "${cleanedTitle}"`);
    }
    
    // Remove "The " prefix from site label for tax product search (e.g., "The Double" becomes "Double")
    let cleanedSiteLabel = siteLabel;
    if (cleanedSiteLabel.startsWith('The ')) {
      cleanedSiteLabel = cleanedSiteLabel.substring(4); // Remove "The " (4 characters)
      log(`Removed "The" prefix from site label: "${siteLabel}" -> "${cleanedSiteLabel}"`);
    }
    
    // Use Weekend menus for NB City locations in two cases:
    // 1. Product title starts with "Weekend" (from weekend collection)
    // 2. Booking date is Memorial Day or Labor Day holiday
    // This ensures Toast can properly sync wristband fees
    const isNbCity = ['Gruene', 'Comal', 'Double'].includes(cleanedSiteLabel);
    const hasWeekendSuffix = /(\(Weekend\))\s*$/i.test(cleanedSiteLabel);
    const isWeekendProduct = /^Weekend\b/i.test(cleanedTitle);
    
    if (isNbCity && !hasWeekendSuffix) {
      // Check if product is from weekend collection OR if it's a holiday booking
      const bookingDate = getBookingDate();
      const isHolidayBooking = bookingDate && isHolidayRequiringWeekendMenu(bookingDate);
      
      if (isWeekendProduct || isHolidayBooking) {
        cleanedSiteLabel = `${cleanedSiteLabel} (Weekend)`;
        if (isWeekendProduct) {
          log(`Weekend product detected - using Weekend menu: "${siteLabel}" -> "${cleanedSiteLabel}"`);
        } else {
          log(`Holiday booking detected - using Weekend menu: "${siteLabel}" -> "${cleanedSiteLabel}"`);
        }
      } else {
        log(`Regular booking - using standard menu: "${cleanedSiteLabel}"`);
      }
    }
    
    return `${cleanedTitle}_tax_${cleanedSiteLabel}`;
  }

  function normalizeForComparison(value) {
    return (value || '')
      .toString()
      // Decode common HTML entities before comparing (Shopify can return encoded titles)
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/[_\s]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function buildTaxProductHandle(taxProductTitle) {
    return taxProductTitle
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function getWristbandHandleForLocation(location) {
    if (!location) return null;
    return WRISTBAND_PRODUCTS[normalizeLocationName(location)] || null;
  }

  function wristbandExistsInCart(cart, location) {
    if (!cart || !Array.isArray(cart.items) || !location) return false;
    const normalizedLocation = location.toLowerCase();
    const expectedHandle = getWristbandHandleForLocation(location);
    return cart.items.some(item => {
      if (!item) return false;
      const handleMatches = expectedHandle && item.handle === expectedHandle;
      const propertyMatches = item.properties && (
        item.properties._wristband_fee_location?.toLowerCase?.() === normalizedLocation ||
        item.properties._wristband_fee === 'true'
      );
      return handleMatches || propertyMatches;
    });
  }

  // --- Normalize known location synonyms (e.g., "Double" -> "The Double") ---
  function normalizeLocationName(loc) {
    if (!loc) return loc;
    const s = String(loc).trim();
    if (/^double$/i.test(s) || /^the\s+double$/i.test(s)) return 'The Double';
    return s;
  }

  // --- Resolve booking date from cart items for a given location (fallback when localStorage missing) ---
  function getStoredDateFromCart(cart, location) {
    if (!cart || !Array.isArray(cart.items) || !location) return null;
    const target = normalizeLocationName(location);
    const itemsAtLocation = cart.items.filter(item => {
      if (!item) return false;
      // skip fee items
      if (item.properties && (item.properties._auto_tax || item.properties._wristband_fee || item.properties._tax_fee || item.properties._fee_product)) return false;
      const itemLoc = normalizeLocationName(getSiteLabelForProduct(item));
      return itemLoc === target;
    });
    for (const item of itemsAtLocation) {
      const props = item.properties || {};
      // Prefer raw ISO date when available
      if (typeof props['Booking Date Raw'] === 'string' && props['Booking Date Raw'].match(/^\d{4}-\d{2}-\d{2}$/)) {
        return props['Booking Date Raw'];
      }
      // Try parsing human-readable Booking Date like "Monday, May 25, 2026"
      if (typeof props['Booking Date'] === 'string' && props['Booking Date'].trim()) {
        const parsed = new Date(props['Booking Date']);
        if (!isNaN(parsed.getTime())) {
          const y = parsed.getFullYear();
          const m = String(parsed.getMonth()+1).padStart(2,'0');
          const d = String(parsed.getDate()).padStart(2,'0');
          return `${y}-${m}-${d}`;
        }
      }
    }
    return null;
  }

  // --- Product type eligibility for wristbands ---
  function productTypeEligible(productType) {
    const t = String(productType || '').trim().toLowerCase();
    
    // Remove "weekend " prefix if present (e.g., "Weekend Tubes" -> "tubes")
    const cleaned = t.replace(/^weekend\s+/i, '');
    
    // Accept common variations to avoid silent mismatches
    return [
      'tube','tubes','tubing',
      'raft','rafts','rafting'
    ].includes(cleaned);
  }

  // Determine if a location has at least one eligible main item (non-tax, non-fee)
  function hasEligibleItemForLocation(cart, location) {
    if (!cart || !Array.isArray(cart.items) || !location) return false;
    return cart.items.some(item => {
      if (!item) return false;
      // skip tax, wristband, and other fee products
      if (item.properties && (item.properties._auto_tax || item.properties._wristband_fee || item.properties._tax_fee || item.properties._fee_product)) return false;
      const itemLoc = getSiteLabelForProduct(item);
      if (!itemLoc || itemLoc !== location) return false;
      return productTypeEligible(item.product_type);
    });
  }

  // --- Regex for matching tax products ---
  const TAX_PRODUCT_REGEX = /^(.+?)_tax_(Gruene|Comal|Double|Huaco|Riverbank|Gruene \(Weekend\)|Comal \(Weekend\)|Double \(Weekend\))$/;

  // --- Get current cart ---
  async function getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      return response.ok ? await response.json() : null;
    } catch (e) {
      log('Error fetching cart:', e.message);
      return null;
    }
  }

  // --- Remove duplicate main products (same variant) keeping the one WITH booking properties ---
  async function cleanupDuplicateMainProducts() {
    try {
      const cart = await getCurrentCart();
      if (!cart || !cart.items || !cart.items.length) return;

      // Group items by variant_id excluding tax and wristband and fee products
      const groups = {};
      for (const item of cart.items) {
        // Skip auto tax products
        if (item.properties && item.properties._auto_tax) continue;
        // Skip wristband fee products (identified by handle starting with 'wristband-' or _wristband_fee flag)
        if (item.handle && item.handle.startsWith('wristband')) continue;
        if (item.properties && item.properties._wristband_fee) continue;
        // Skip obvious fee products with our internal flags
        if (item.properties && (item.properties._tax_fee || item.properties._fee_product)) continue;

        const variantId = item.variant_id || item.id; // fallback
        if (!groups[variantId]) groups[variantId] = [];
        groups[variantId].push(item);
      }

      let removedCount = 0;
      for (const items of Object.values(groups)) {
        if (items.length < 2) continue;
        const withBooking = items.filter(i => i.properties && i.properties['Booking Location']);
        if (!withBooking.length) continue;
        const withoutBooking = items.filter(i => !i.properties || !i.properties['Booking Location']);
        for (const dup of withoutBooking) {
          if (!dup.key) continue;
          log(`Removing duplicate main product (no booking properties) for variant ${dup.variant_id}: '${dup.title}'`);
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: dup.key, quantity: 0 })
          });
          removedCount += 1;
        }
      }

      if (removedCount) {
        log(`Cleanup removed ${removedCount} duplicate main product line(s).`);
      }
    } catch (e) {
      log('Error cleaning duplicate main products: ' + e.message);
    }
  }

  function findMissingTaxEntries(cart) {
    const missing = [];
    if (!cart || !Array.isArray(cart.items)) {
      return missing;
    }

    for (const item of cart.items) {
      if (!item) continue;
      if (item.properties && item.properties._auto_tax) {
        continue;
      }
      const siteLabel = getSiteLabelForProduct(item);
      if (!siteLabel) {
        continue;
      }

      const taxProductTitle = buildTaxProductTitle(item.product_title, siteLabel);
      const normalizedTaxTitle = normalizeForComparison(taxProductTitle);
      const taxExists = cart.items.some(cartItem => {
        if (!cartItem || !cartItem.properties || !cartItem.properties._auto_tax) return false;
        if (cartItem.properties._tax_for_item !== item.handle) return false;
        if (!cartItem.title) return false;
        return normalizeForComparison(cartItem.title) === normalizedTaxTitle;
      });

      if (!taxExists) {
        missing.push({ item, taxProductTitle });
      }
    }

    return missing;
  }

  function findMissingWristbandEntries(cart) {
    const missing = [];
    if (!cart || !Array.isArray(cart.items)) {
      return missing;
    }

    const processedLocations = new Set();

    for (const item of cart.items) {
      if (!item) continue;
      if (item.properties && item.properties._auto_tax) continue;

      const rawLoc = getSiteLabelForProduct(item);
      if (!rawLoc) continue;
      const location = normalizeLocationName(rawLoc);
      if (processedLocations.has(location)) continue;

      processedLocations.add(location);

      if (pendingWristbandAdditions.has(location) || externalWristbandAdditions.has(location)) {
        log(`Wristband fee handling already in progress for '${location}', skipping duplicate detection.`);
        continue;
      }

      if (wasWristbandRecentlyAdded(location)) {
        log(`Wristband fee for '${location}' was recently added, waiting for cart sync before rechecking.`);
        continue;
      }

      // Gate by product type: only apply wristband if there is at least one eligible item at this location
      const eligibleAtLocation = hasEligibleItemForLocation(cart, location);
      if (!eligibleAtLocation) {
        log(`No eligible (Tubes/Rafts) items at '${location}' - skipping wristband for this location.`);
        continue;
      }

      // Check if wristband should be required based on location and date rules.
      // Pass stored booking date from cart as fallback when localStorage is unavailable.
      const storedDate = getStoredDateFromCart(cart, location);
      if (!shouldAddWristband(location, storedDate)) {
        log(`Wristband not required for '${location}' based on date rules, skipping.`);
        continue;
      }

      if (!wristbandExistsInCart(cart, location)) {
        missing.push({ location });
      }
    }

    return missing;
  }

  function findItemsMissingBookingLocation(cart) {
    const missing = [];
    if (!cart || !Array.isArray(cart.items)) {
      return missing;
    }

    for (const item of cart.items) {
      if (!item) continue;
      if (item.properties && (item.properties._auto_tax || item.properties._wristband_fee)) continue;
      
      // Skip validation for Season Pass and River Access Day Pass products
      const productTitle = item.product_title || item.title || '';
      if (productTitle.includes('Season Pass') || productTitle.toLowerCase().includes('river access day pass')) {
        continue;
      }
      
      const location = getSiteLabelForProduct(item);
      if (!location) {
        missing.push(item);
      }
    }

    return missing;
  }

  async function fetchTaxProductByHandle(handle, expectedTitle) {
    try {
      if (!handle) return null;
      log(`Attempting to fetch tax product by handle: ${handle}`);
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        log(`No product found for handle '${handle}'.`);
        return null;
      }
      const data = await response.json();
      if (!TAX_PRODUCT_REGEX.test(data.title)) {
        log(`Product '${data.title}' fetched by handle did not match tax regex. Ignoring.`);
        return null;
      }
      const normalizedExpected = normalizeForComparison(expectedTitle);
      if (normalizedExpected && normalizeForComparison(data.title) !== normalizedExpected) {
        log(`Product '${data.title}' fetched by handle did not match expected title '${expectedTitle}'. Ignoring.`);
        return null;
      }
      return {
        title: data.title,
        handle,
        variants: data.variants
      };
    } catch (e) {
      log(`Error fetching product by handle '${handle}': ${e.message}`);
      return null;
    }
  }

  async function fetchTaxProductBySearch(taxProductTitle, forItemHandle) {
    try {
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(taxProductTitle)}&resources[type]=product&resources[limit]=5`;
      log(`Searching for tax product with URL: ${searchUrl}`);
      const searchRes = await fetch(searchUrl);
      if (!searchRes.ok) {
        log(`Search request failed with status ${searchRes.status}`);
        return null;
      }
      const searchData = await searchRes.json();
      log('Search result:', searchData);
      const candidates = searchData?.resources?.results?.products || [];
      if (!candidates.length) {
        log(`Search returned no product candidates for '${taxProductTitle}'`);
        return null;
      }

      const normalizedExpected = normalizeForComparison(taxProductTitle);
      const candidate = candidates.find(product => {
        if (!product || !product.title) return false;
        if (product.handle === forItemHandle) {
          log(`Skipping search candidate '${product.title}' because handle matches main product '${forItemHandle}'.`);
          return false;
        }
        const normalizedTitle = normalizeForComparison(product.title);
        const matchesExpected = normalizedTitle === normalizedExpected;
        const matchesRegex = TAX_PRODUCT_REGEX.test(product.title);
        const handleLooksLikeTax = product.handle && product.handle.includes('_tax_');
        return matchesExpected || (matchesRegex && handleLooksLikeTax);
      });

      if (!candidate) {
        log(`No matching tax product candidate found for '${taxProductTitle}'.`);
        return null;
      }

      log(`Selected tax product candidate from search: ${candidate.title} (handle: ${candidate.handle})`);
      const prodRes = await fetch(`/products/${candidate.handle}.js`);
      if (!prodRes.ok) {
        log(`Failed to fetch product data for candidate handle '${candidate.handle}'`);
        return null;
      }
      const prodData = await prodRes.json();
      if (!TAX_PRODUCT_REGEX.test(prodData.title)) {
        log(`Search candidate '${prodData.title}' does not match tax regex. Ignoring.`);
        return null;
      }
      return {
        title: prodData.title,
        handle: candidate.handle,
        variants: prodData.variants
      };
    } catch (e) {
      log('Error searching for tax product: ' + e.message);
      return null;
    }
  }

  async function resolveTaxProduct(taxProductTitle, forItemHandle) {
    const handleCandidate = buildTaxProductHandle(taxProductTitle);
    let productData = await fetchTaxProductByHandle(handleCandidate, taxProductTitle);
    if (!productData) {
      productData = await fetchTaxProductBySearch(taxProductTitle, forItemHandle);
    }
    return productData;
  }

  let isEnsuringCheckoutTax = false;

  async function addWristbandFeeForLocation(location) {
    if (!location) {
      return false;
    }

    if (pendingWristbandAdditions.has(location)) {
      log(`Wristband addition already in progress for '${location}', skipping duplicate request.`);
      return false;
    }

    if (externalWristbandAdditions.has(location)) {
      log(`External wristband addition in progress for '${location}', skipping duplicate request.`);
      return false;
    }

    const normalizedLocation = normalizeLocationName(location);
    const handle = getWristbandHandleForLocation(normalizedLocation);
    if (!handle) {
      log(`No wristband handle configured for location '${normalizedLocation}'.`);
      return false;
    }

    pendingWristbandAdditions.add(location);

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        log(`Wristband product not found for handle '${handle}'.`);
        return false;
      }

      const product = await response.json();
      if (!product.variants || !product.variants[0]) {
        log(`Invalid wristband product data for handle '${handle}'.`);
        return false;
      }

      const properties = {
        '_wristband_fee_location': normalizedLocation,
        '_wristband_fee': 'true',
        'City-required item': `Wristband Fee for ${normalizedLocation}`,
        '_timestamp': Date.now().toString()
      };

      // Attach booking date to wristband item when available so later cleanup/validation
      // can determine whether the wristband is still required even if localStorage is missing.
      try {
        let bookingDate = getBookingDate();
        if (!bookingDate) {
          const cart = await getCurrentCart();
          bookingDate = getStoredDateFromCart(cart, normalizedLocation);
        }
        if (bookingDate) {
          properties._wristband_fee_date = bookingDate;
        }
      } catch (e) {
        log('Error attaching booking date to wristband properties: ' + e.message);
      }

      if (normalizedLocation === 'TEST') {
        properties._test_collection = 'true';
      }

      const formData = {
        items: [{
          id: product.variants[0].id,
          quantity: 1,
          properties
        }]
      };

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!addResponse.ok) {
        const errorText = await addResponse.text();
        log(`Failed to add wristband fee for ${location}: ${addResponse.status} - ${errorText}`);
        return false;
      }

      log(`Added wristband fee for ${normalizedLocation}.`);
      markRecentWristbandAddition(normalizedLocation);
      return true;
    } catch (e) {
      log(`Error adding wristband fee for ${normalizedLocation}: ${e.message}`);
      return false;
    } finally {
      pendingWristbandAdditions.delete(location);
    }
  }

  function proceedThroughCheckout(button) {
    if (!button) return;
    if (button.tagName === 'A' && button.href) {
      window.location.href = button.href;
      return;
    }
    if (button.form) {
      let tempInput = null;
      if (button.name) {
        tempInput = document.createElement('input');
        tempInput.type = 'hidden';
        tempInput.name = button.name;
        tempInput.value = button.value || 'on';
        button.form.appendChild(tempInput);
      }
      button.dataset.skipTaxGuard = 'true';
      button.form.submit();
      if (tempInput) {
        setTimeout(() => tempInput.remove(), 0);
      }
      return;
    }
    if (button.href) {
      window.location.href = button.href;
      return;
    }
    button.dataset.skipTaxGuard = 'true';
    setTimeout(() => button.click(), 0);
  }

  function disableCheckoutButton(button) {
    const wasDisabled = button.hasAttribute('disabled') || button.dataset.disabled === 'true' || button.disabled;
    const hadAriaDisabled = button.hasAttribute('aria-disabled');
    const previousAriaValue = button.getAttribute('aria-disabled');
    const previousPointerEvents = button.style.pointerEvents;
    const previousOpacity = button.style.opacity;
    const previousFilter = button.style.filter;
    const previousCursor = button.style.cursor;
    const previousHtml = button.innerHTML;
    const isButtonLike = button.tagName === 'BUTTON' || button.tagName === 'INPUT';
    button.setAttribute('aria-disabled', 'true');
    button.style.pointerEvents = 'none';
    button.style.opacity = '0.6';
    button.style.filter = 'grayscale(60%)';
    button.style.cursor = 'not-allowed';
    if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
      button.disabled = true;
      if (button.tagName === 'BUTTON') {
        button.innerHTML = '<span style="display:inline-flex;align-items:center;gap:0.5rem;"><span>Securing required items…</span></span>';
      }
    }

    let statusMessage = null;
    if (!button.nextElementSibling || !button.nextElementSibling.classList || !button.nextElementSibling.classList.contains('auto-tax-checkout-status')) {
      statusMessage = document.createElement('div');
      statusMessage.className = 'auto-tax-checkout-status';
      statusMessage.textContent = 'Please wait, we are locking in the required wristband and tax items…';
      statusMessage.style.marginTop = '0.75rem';
      statusMessage.style.fontSize = '0.85em';
      statusMessage.style.fontStyle = 'italic';
      statusMessage.style.color = '#444';
      button.insertAdjacentElement('afterend', statusMessage);
    } else {
      statusMessage = button.nextElementSibling;
    }

    return (options = {}) => {
      const {
        keepStatus = false,
        message,
        messageType = 'info',
        highlightButton = false
      } = options || {};

      button.style.pointerEvents = previousPointerEvents;
      button.style.opacity = previousOpacity;
      button.style.filter = previousFilter;
      button.style.cursor = previousCursor;
      if (isButtonLike && button.tagName === 'BUTTON') {
        button.innerHTML = previousHtml;
      }
      if (!wasDisabled) {
        if (!hadAriaDisabled) {
          button.removeAttribute('aria-disabled');
        } else if (previousAriaValue !== null) {
          button.setAttribute('aria-disabled', previousAriaValue);
        }
        if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
          button.disabled = false;
        }
      }
      if (statusMessage) {
        if (typeof message === 'string') {
          statusMessage.textContent = message;
        }
        const isError = messageType === 'error';
        statusMessage.style.color = isError ? '#b00020' : '#444';
        statusMessage.style.fontWeight = isError ? '600' : 'normal';
        statusMessage.style.opacity = '1';
        if (!keepStatus && statusMessage.parentNode) {
          statusMessage.parentNode.removeChild(statusMessage);
        }
      }
      if (highlightButton) {
        const previousOutline = button.style.outline;
        const previousOutlineOffset = button.style.outlineOffset;
        button.style.outline = '2px solid #b00020';
        button.style.outlineOffset = '2px';
        setTimeout(() => {
          button.style.outline = previousOutline;
          button.style.outlineOffset = previousOutlineOffset;
        }, 1500);
      }
    };
  }

  async function waitForTaxProcessingCompletion(maxWaitMs = 2000) {
    const start = Date.now();
    while (true) {
      if (!isProcessingTaxCart && pendingTaxAdditions.size === 0 && pendingWristbandAdditions.size === 0 && externalWristbandAdditions.size === 0) {
        return true;
      }
      if (Date.now() - start > maxWaitMs) {
        return false;
      }
      await new Promise(resolve => setTimeout(resolve, 75));
    }
  }

  async function handleCheckoutClick(event, button) {
    if (!button) return;

    if (button.dataset.skipTaxGuard === 'true') {
      delete button.dataset.skipTaxGuard;
      return;
    }

    // Always prevent default first to block any race condition
    event.preventDefault();
    event.stopPropagation();
    // stopImmediatePropagation is supported in all modern browsers and IE9+, but add safety check
    if (event.stopImmediatePropagation) {
      event.stopImmediatePropagation();
    }

    if (isEnsuringCheckoutTax) {
      log('Checkout guard already running; ignoring duplicate click.');
      return;
    }

    isEnsuringCheckoutTax = true;
    const restoreButtonState = disableCheckoutButton(button);
    let restoreOptions;
    try {
      let cart = await getCurrentCart();
      let missingTaxEntries = findMissingTaxEntries(cart);
      let missingWristbandEntries = findMissingWristbandEntries(cart);
      let missingBookingItems = findItemsMissingBookingLocation(cart);

      if (!missingTaxEntries.length && !missingWristbandEntries.length && !missingBookingItems.length) {
        log('Checkout guard: all required tax, wristband, and booking requirements satisfied. Proceeding.');
        proceedThroughCheckout(button);
        return;
      }

      log(`Checkout guard: detected ${missingTaxEntries.length} missing tax product(s), ${missingWristbandEntries.length} missing wristband fee(s), and ${missingBookingItems.length} item(s) lacking booking details. Attempting to resolve before checkout.`);
      await processCartForAutoTax();
      const firstWaitComplete = await waitForTaxProcessingCompletion();
      if (!firstWaitComplete) {
        log('Checkout guard: timeout waiting for first tax processing cycle to finish.');
      }
      cart = await getCurrentCart();
      missingTaxEntries = findMissingTaxEntries(cart);
      missingWristbandEntries = findMissingWristbandEntries(cart);
      missingBookingItems = findItemsMissingBookingLocation(cart);

      if (!missingTaxEntries.length && !missingWristbandEntries.length && !missingBookingItems.length) {
        log('Checkout guard: required items added successfully after first sync. Proceeding.');
      } else {
        if (missingBookingItems.length) {
          log('Checkout guard: blocking checkout due to missing booking details.');
          applyInitialCheckoutBlock({ force: true });
          updateInitialStatusMessages('Booking details are required before checkout. Please edit each reservation to choose a location.', { tone: 'error' });
          restoreOptions = {
            keepStatus: true,
            message: 'We still need booking details for one or more items. Please edit each reservation to choose a location before checking out.',
            messageType: 'error',
            highlightButton: true
          };
          return;
        }

        log('Checkout guard: still missing required tax or wristband items after first retry, attempting one more sync.');
        await processCartForAutoTax();
        const secondWaitComplete = await waitForTaxProcessingCompletion();
        if (!secondWaitComplete) {
          log('Checkout guard: timeout waiting for second tax processing cycle to finish.');
        }
        cart = await getCurrentCart();
        missingTaxEntries = findMissingTaxEntries(cart);
        missingWristbandEntries = findMissingWristbandEntries(cart);
        missingBookingItems = findItemsMissingBookingLocation(cart);
        if (!missingTaxEntries.length && !missingWristbandEntries.length && !missingBookingItems.length) {
          log('Checkout guard: required items finally added after second sync. Proceeding.');
        } else {
          if (missingBookingItems.length) {
            log('Checkout guard: blocking checkout due to unresolved booking details.');
            applyInitialCheckoutBlock({ force: true });
            updateInitialStatusMessages('Booking details are required before checkout. Please edit each reservation to choose a location.', { tone: 'error' });
            restoreOptions = {
              keepStatus: true,
              message: 'We still need booking details for one or more items. Please edit each reservation to choose a location before checking out.',
              messageType: 'error',
              highlightButton: true
            };
            return;
          }

          log('Checkout guard: still missing some required wristband or tax items after second retry. Blocking checkout until resolved.');
          applyInitialCheckoutBlock({ force: true });
          updateInitialStatusMessages('Verifying required wristband and tax items…');
          restoreOptions = {
            keepStatus: true,
            message: 'We still need to add the required city wristband and tax items. Please wait a few seconds and try again.',
            messageType: 'error',
            highlightButton: true
          };
          return;
        }
      }

      proceedThroughCheckout(button);
      return;
    } catch (e) {
      log('Checkout guard error: ' + e.message);
      applyInitialCheckoutBlock({ force: true });
      updateInitialStatusMessages('We ran into an issue securing the required items. Please wait a moment and try again.', { tone: 'error' });
      restoreOptions = {
        keepStatus: true,
        message: 'We ran into an issue securing the required items. Please wait a moment and try again.',
        messageType: 'error',
        highlightButton: true
      };
    } finally {
      restoreButtonState(restoreOptions);
      isEnsuringCheckoutTax = false;
    }
  }

  // --- Add tax product to cart (quantity 1 per main product) ---
  async function addTaxProductToCart(taxProductTitle, forItemHandle, forItemTitle) {
    try {
      if (pendingTaxAdditions.has(forItemHandle)) {
        log(`Tax addition already in progress for '${forItemHandle}', skipping duplicate request.`);
        return false;
      }
      pendingTaxAdditions.add(forItemHandle);

      const taxProduct = await resolveTaxProduct(taxProductTitle, forItemHandle);
      if (!taxProduct || !Array.isArray(taxProduct.variants) || !taxProduct.variants.length) {
        log(`Tax product data missing or invalid for '${taxProductTitle}'. Skipping.`);
        return false;
      }

      const variant = taxProduct.variants[0];
      log(`Adding variant ID: ${variant.id} to cart for tax product '${taxProduct.title}'.`);

      // Fresh cart check immediately before adding — prevents duplicate adds from timing/event loops
      const freshCart = await getCurrentCart();
      if (freshCart) {
        const normalizedTaxTitle = normalizeForComparison(taxProductTitle);
        const alreadyInCart = freshCart.items.some(cartItem =>
          cartItem.properties &&
          cartItem.properties._auto_tax &&
          cartItem.properties._tax_for_item === forItemHandle &&
          normalizeForComparison(cartItem.title) === normalizedTaxTitle
        );
        if (alreadyInCart) {
          log(`Tax '${taxProductTitle}' already in cart for '${forItemHandle}', skipping duplicate.`);
          return false;
        }
      }

      const formData = {
        items: [{
          id: variant.id,
          quantity: 1,
          properties: {
            '_auto_tax': 'true',
            '_tax_for_item': forItemHandle,
            '_tax_for_item_title': forItemTitle,
            '_timestamp': Date.now().toString()
          }
        }]
      };
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      lastAutoTaxCartUpdate = Date.now();
      log(`Added tax product: ${taxProductTitle} for ${forItemTitle}`);
      return true;
    } catch (e) {
      log('Error adding tax product:', e.message);
      return false;
    } finally {
      pendingTaxAdditions.delete(forItemHandle);
    }
  }

  // --- Remove orphaned tax products ---
  async function cleanupTaxProducts() {
    try {
      const cart = await getCurrentCart();
      if (!cart) return;
      const mainItems = cart.items.filter(item => !item.properties || !item.properties._auto_tax);
      const taxItems = cart.items.filter(item => item.properties && item.properties._auto_tax && TAX_PRODUCT_REGEX.test(item.title));
      for (const taxItem of taxItems) {
        const correspondingItem = mainItems.find(item => item.handle === taxItem.properties._tax_for_item);
        if (!correspondingItem) {
          if (!taxItem.key) continue;
          log(`Removing orphaned tax: ${taxItem.title}`);
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: taxItem.key, quantity: 0 })
          });
        }
      }
    } catch (e) {
      log('Error cleaning up tax products:', e.message);
    }
  }
  
  // --- Remove wristbands that are no longer required ---
  async function cleanupWristbandProducts() {
    try {
      const cart = await getCurrentCart();
      if (!cart || !cart.items) return;
      
      // Get all wristband items in cart
      const wristbandItems = cart.items.filter(item => 
        item.properties && item.properties._wristband_fee
      );
      
      if (!wristbandItems.length) return;
      
      // Check each wristband to see if it should still be in cart
      for (const wristbandItem of wristbandItems) {
        const location = wristbandItem.properties._wristband_fee_location;
        if (!location) continue;
        
        // Get the stored date from the wristband properties (if available)
        const storedDate = wristbandItem.properties._wristband_fee_date;
        
        // Check if wristband should still be required by date/location AND there remains an eligible item at that location
        const stillHasEligible = hasEligibleItemForLocation(cart, location);
        // Pass the stored date so we can validate even without localStorage
        if (!(stillHasEligible && shouldAddWristband(location, storedDate))) {
          if (!wristbandItem.key) continue;
          log(`Removing wristband for '${location}' - no longer required based on date rules`);
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: wristbandItem.key, quantity: 0 })
          });
        }
      }
    } catch (e) {
      log('Error cleaning up wristband products:', e.message);
    }
  }

  // --- Main logic: add tax products for main items ---
  async function processCartForAutoTax() {
    if (isProcessingTaxCart) {
      queuedCartProcessing = true;
      log('processCartForAutoTax already running, queuing another pass.');
      return;
    }

    isProcessingTaxCart = true;
    try {
      let shouldRecheck = false;
      do {
        shouldRecheck = false;
        await cleanupDuplicateMainProducts();
        await cleanupWristbandProducts();
        const cart = await getCurrentCart();
        log('Cart items at processCartForAutoTax:', cart && cart.items ? cart.items : cart);
        if (!cart || !cart.items.length) {
          applyInitialCheckoutBlock({ force: true });
          updateInitialStatusMessages('Add an experience to your cart before checking out.', { tone: 'info' });
          break;
        }

        const missingTaxEntries = findMissingTaxEntries(cart);
        const missingWristbandEntries = findMissingWristbandEntries(cart);
        const missingBookingItems = findItemsMissingBookingLocation(cart);

        if (!missingTaxEntries.length && !missingWristbandEntries.length && !missingBookingItems.length) {
          log('All required tax and wristband items are present.');
          // Wait for final verification before unlocking - extra 2 seconds to ensure cart is fully ready
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Re-verify one final time after the delay to ensure nothing changed
          const finalCart = await getCurrentCart();
          const finalMissingTax = findMissingTaxEntries(finalCart);
          const finalMissingWristband = findMissingWristbandEntries(finalCart);
          const finalMissingBooking = findItemsMissingBookingLocation(finalCart);
          
          if (!finalMissingTax.length && !finalMissingWristband.length && !finalMissingBooking.length) {
            log('Final verification passed - cart is ready for checkout.');
            releaseInitialCheckoutBlockIfLocked({ 
              message: 'All required items are secured. You may proceed to checkout.', 
              tone: 'success',
              delayMs: 1500 // Additional delay before unlock
            });
          } else {
            log('Final verification failed - re-processing cart.');
            shouldRecheck = true;
          }
          break;
        }

        if (missingTaxEntries.length) {
          log(`Found ${missingTaxEntries.length} item(s) missing tax products.`);
        }
        if (missingWristbandEntries.length) {
          log(`Found ${missingWristbandEntries.length} location(s) missing wristband fees.`);
        }
        if (!initialCheckoutLocked && (missingTaxEntries.length || missingWristbandEntries.length || missingBookingItems.length)) {
          applyInitialCheckoutBlock({ force: true });
        }
        if (missingBookingItems.length) {
          log(`Found ${missingBookingItems.length} cart item(s) missing booking details.`);
          updateInitialStatusMessages('Booking details are required before checkout. Please edit each reservation to choose a location.', { tone: 'error' });
        } else if (initialCheckoutLocked) {
          updateInitialStatusMessages('Verifying required wristband and tax items…');
        }

        for (const entry of missingTaxEntries) {
          const { item, taxProductTitle } = entry;
          log(`Adding missing tax product '${taxProductTitle}' for cart item '${item.title}'.`);
          const added = await addTaxProductToCart(taxProductTitle, item.handle, item.title);
          if (added) {
            shouldRecheck = true;
            await new Promise(resolve => setTimeout(resolve, 150));
          }
        }

        for (const entry of missingWristbandEntries) {
          const { location } = entry;
          log(`Adding missing wristband fee for location '${location}'.`);
          const added = await addWristbandFeeForLocation(location);
          if (added) {
            shouldRecheck = true;
            await new Promise(resolve => setTimeout(resolve, 150));
          }
        }
      } while (shouldRecheck);
    } finally {
      isProcessingTaxCart = false;
      if (queuedCartProcessing) {
        queuedCartProcessing = false;
        setTimeout(() => processCartForAutoTax(), 100);
      }
    }
  }

  // --- Monitor cart changes ---
  function setupCartMonitoring() {
    log('Setting up auto tax monitoring...');
    document.addEventListener('click', function(event) {
      const addToCartButton = event.target.closest('[name="add"], .btn-product-add, .product-form__cart-submit, [data-add-to-cart]');
      if (addToCartButton) {
        log('Add to cart detected - will process auto tax after product is added');
        setTimeout(() => processCartForAutoTax(), 200);
      }
    });
    // Use capture phase with highest priority to intercept checkout clicks early
    document.addEventListener('click', function(event) {
      const checkoutButton = event.target.closest('[href*="/checkout"], [name="checkout"], [data-checkout], .checkout-btn, .cart__checkout');
      if (checkoutButton) {
        // Immediately prevent any default action
        event.preventDefault();
        event.stopPropagation();
        // stopImmediatePropagation is supported in all modern browsers and IE9+
        if (event.stopImmediatePropagation) {
          event.stopImmediatePropagation();
        }
        handleCheckoutClick(event, checkoutButton);
      }
    }, true);
    
    // Add a secondary listener as backup in case the first is bypassed
    document.addEventListener('click', function(event) {
      const checkoutButton = event.target.closest('[href*="/checkout"], [name="checkout"], [data-checkout], .checkout-btn, .cart__checkout');
      if (checkoutButton && checkoutButton.dataset.skipTaxGuard !== 'true') {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) {
          event.stopImmediatePropagation();
        }
      }
    }, false);
    document.addEventListener('cart:updated', function() {
      if (Date.now() - lastAutoTaxCartUpdate < AUTO_TAX_UPDATE_COOLDOWN_MS) {
        log('Skipping cart:updated reprocessing - within auto-tax cooldown window');
        return;
      }
      log('Cart updated - processing auto tax');
      setTimeout(() => {
        cleanupTaxProducts();
        cleanupWristbandProducts();
        processCartForAutoTax();
      }, 100);
    });
    if (window.location.pathname.includes('/cart')) {
      // Run cleanup immediately on cart page load, then again after 1 second
      cleanupTaxProducts();
      cleanupWristbandProducts();
      setTimeout(() => processCartForAutoTax(), 1000);
    }
  }

  // --- Init ---
  function init() {
    if (!ENABLED) return;
    log('Auto Tax Products initialized');
    applyInitialCheckoutBlock();
    setupCartMonitoring();
    setTimeout(() => processCartForAutoTax(), 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
